import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Form, Modal, Alert, Tabs, Tab, Pagination } from 'react-bootstrap';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import './style.css';

function AdminEmailMarketing({ user }) {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('campaigns');
	const [campaigns, setCampaigns] = useState([]);
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');

	const [showScheduleModal, setShowScheduleModal] = useState(false);
	const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
	const [selectedCampaign, setSelectedCampaign] = useState(null);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [selectAll, setSelectAll] = useState(false);
	const [userSearchTerm, setUserSearchTerm] = useState('');
	const [userTabSearchTerm, setUserTabSearchTerm] = useState('');
	const [userPage, setUserPage] = useState(1);
	const [usersPerPage] = useState(10);
	const [campaignForm, setCampaignForm] = useState({
		scheduled_at: ''
	});

	const [stats, setStats] = useState({
		totalUsers: 0,
		activeUsers: 0,
		totalCampaigns: 0,
		sentCampaigns: 0
	});

	useEffect(() => {
		fetchCampaigns();
		fetchUsers();
		fetchStats();
	}, []);

	const fetchCampaigns = async () => {
		setLoading(true);
		try {
			const response = await axios.get('/admin/email-campaigns', {
				params: {
					page: 1,
					limit: 50
				},
				headers: { Authorization: `Bearer ${user.token}` }
			});
			if (response.data.status) {
				setCampaigns(response.data.data.campaigns || []);
			}
		} catch (error) {
			console.error('Error fetching campaigns:', error);
			setError('Failed to load campaigns');
		} finally {
			setLoading(false);
		}
	};

	const fetchUsers = async () => {
		try {
			const response = await axios.get('/admin/users', {
				headers: { Authorization: `Bearer ${user.token}` }
			});
			if (response.data.status) {
				setUsers(response.data.data || []);
			}
		} catch (error) {
			console.error('Error fetching users:', error);
			setUsers([]);
		}
	};

	const fetchStats = async () => {
		try {
			const response = await axios.get('/admin/email-stats', {
				headers: { Authorization: `Bearer ${user.token}` }
			});
			if (response.data.status) {
				setStats(response.data.data);
			}
		} catch (error) {
			console.error('Error fetching stats:', error);
		}
	};

	const handleSendCampaign = async (campaignId) => {
		if (!window.confirm('Are you sure you want to send this campaign to all active subscribers?')) {
			return;
		}

		try {
			const response = await axios.post(`/admin/email-campaigns/${campaignId}/send`, {}, {
				headers: { Authorization: `Bearer ${user.token}` }
			});
			if (response.data.status) {
				setSuccess('Campaign is being sent!');
				fetchCampaigns();
				fetchStats();
			}
		} catch (error) {
			setError(error.response?.data?.message || 'Failed to send campaign');
		}
	};

	const handleDeleteCampaign = async (campaignId, campaignSubject) => {
		if (!window.confirm(`Are you sure you want to delete the campaign "${campaignSubject}"? This action cannot be undone.`)) {
			return;
		}

		try {
			const response = await axios.delete(`/admin/email-campaigns/${campaignId}`, {
				headers: { Authorization: `Bearer ${user.token}` }
			});
			if (response.data.status) {
				setSuccess('Campaign deleted successfully!');
				fetchCampaigns();
				fetchStats();
			}
		} catch (error) {
			setError(error.response?.data?.message || 'Failed to delete campaign');
		}
	};

	const handleResendCampaign = async (campaignId, campaignSubject) => {
		if (!window.confirm(`Are you sure you want to resend the campaign "${campaignSubject}" to all active subscribers?`)) {
			return;
		}

		try {
			const response = await axios.post(`/admin/email-campaigns/${campaignId}/send`, {}, {
				headers: { Authorization: `Bearer ${user.token}` }
			});
			if (response.data.status) {
				setSuccess('Campaign is being resent!');
				fetchCampaigns();
				fetchStats();
			}
		} catch (error) {
			setError(error.response?.data?.message || 'Failed to resend campaign');
		}
	};

	const handleToggleEmailPreference = async (userId, field, value) => {
		try {
			const response = await axios.patch(`/admin/users/${userId}/email-preferences`, {
				[field]: value
			}, {
				headers: { Authorization: `Bearer ${user.token}` }
			});
			if (response.data.status) {
				setSuccess('Email preferences updated!');
				fetchUsers();
			}
		} catch (error) {
			setError(error.response?.data?.message || 'Failed to update preferences');
		}
	};

	const handleScheduleCampaign = (campaign) => {
		setSelectedCampaign(campaign);
		setShowScheduleModal(true);
	};

	const handleSaveSchedule = async () => {
		if (!campaignForm.scheduled_at) {
			setError('Please select a date and time');
			return;
		}

		try {
			const response = await axios.patch(
				`/admin/email-campaigns/${selectedCampaign.id}/schedule`,
				{ 
					scheduled_at: campaignForm.scheduled_at,
					selected_users: selectedUsers.length > 0 ? selectedUsers : undefined
				},
				{ headers: { Authorization: `Bearer ${user.token}` } }
			);
			if (response.data.status) {
				setSuccess('Campaign scheduled successfully!');
				setShowScheduleModal(false);
				fetchCampaigns();
				setCampaignForm({ scheduled_at: '' });
				setSelectedUsers([]);
				setSelectAll(false);
			}
		} catch (error) {
			setError(error.response?.data?.message || 'Failed to schedule campaign');
		}
	};

	const handleToggleUserSelection = (userId) => {
		setSelectedUsers(prev => {
			if (prev.includes(userId)) {
				return prev.filter(id => id !== userId);
			} else {
				return [...prev, userId];
			}
		});
	};

	const handleSelectAllUsers = () => {
		if (selectAll) {
			setSelectedUsers([]);
			setSelectAll(false);
		} else {
			const activeUsers = getFilteredUsers().filter(u => u.email_breaking_news !== false && u.email_verified === 1);
			setSelectedUsers(activeUsers.map(u => u.id));
			setSelectAll(true);
		}
	};

	const getFilteredUsers = () => {
		if (!userSearchTerm) return users;
		return users.filter(u => 
			u.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
			u.full_name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
			u.first_name?.toLowerCase().includes(userSearchTerm.toLowerCase())
		);
	};

	const getFilteredUsersForTab = () => {
		if (!userTabSearchTerm) return users;
		return users.filter(u => 
			u.email?.toLowerCase().includes(userTabSearchTerm.toLowerCase()) ||
			u.full_name?.toLowerCase().includes(userTabSearchTerm.toLowerCase()) ||
			u.first_name?.toLowerCase().includes(userTabSearchTerm.toLowerCase())
		);
	};

	const openUserSelectionModal = () => {
		setShowUserSelectionModal(true);
	};

	const closeUserSelectionModal = () => {
		setShowUserSelectionModal(false);
	};

	const getCampaignStatusBadge = (status) => {
		const badges = {
			draft: 'secondary',
			scheduled: 'warning',
			sent: 'success',
			failed: 'danger'
		};
		return <Badge bg={badges[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
	};


	return (
		<Container fluid className="admin-email-marketing py-4">
			<Row className="mb-4">
				<Col>
					<h2 className="mb-0">Email Marketing Management</h2>
					<p className="text-muted">Send breaking news campaigns to registered users</p>
				</Col>
			</Row>

			{error && (
				<Alert variant="danger" onClose={() => setError('')} dismissible>
					{error}
				</Alert>
			)}
			{success && (
				<Alert variant="success" onClose={() => setSuccess('')} dismissible>
					{success}
				</Alert>
			)}

			{/* Stats Cards */}
			<Row className="mb-4">
				<Col md={3}>
					<Card className="stat-card">
						<Card.Body>
							<h3>{stats.totalUsers}</h3>
							<p className="text-muted mb-0">Total Users</p>
						</Card.Body>
					</Card>
				</Col>
				<Col md={3}>
					<Card className="stat-card">
						<Card.Body>
							<h3>{stats.activeUsers}</h3>
							<p className="text-muted mb-0">Verified & Active</p>
						</Card.Body>
					</Card>
				</Col>
				<Col md={3}>
					<Card className="stat-card">
						<Card.Body>
							<h3>{stats.totalCampaigns}</h3>
							<p className="text-muted mb-0">Total Campaigns</p>
						</Card.Body>
					</Card>
				</Col>
				<Col md={3}>
					<Card className="stat-card">
						<Card.Body>
							<h3>{stats.sentCampaigns}</h3>
							<p className="text-muted mb-0">Sent Campaigns</p>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			{/* Tabs */}
			<Card>
				<Card.Body>
					<Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
						{/* Campaigns Tab */}
						<Tab eventKey="campaigns" title="Campaigns">
							<div className="d-flex justify-content-between align-items-center mb-3">
								<h5 className="mb-0">Email Campaigns</h5>
								<Button variant="primary" onClick={() => navigate('/admin-panel/email-marketing/create')}>
									+ Create Campaign
								</Button>
							</div>

							{loading ? (
								<div className="text-center py-5">
									<div className="spinner-border" role="status">
										<span className="visually-hidden">Loading...</span>
									</div>
								</div>
							) : campaigns.length === 0 ? (
								<div className="text-center py-5">
									<p className="text-muted">No campaigns yet. Create your first campaign!</p>
								</div>
							) : (
								<Table responsive hover>
									<thead>
										<tr>
											<th>#</th>
											<th>Subject</th>
											<th>Type</th>
											<th>Status</th>
											<th>Recipients</th>
											<th>Sent</th>
											<th>Clicked</th>
											<th>Created</th>
											<th>Actions</th>
										</tr>
									</thead>
									<tbody>
										{campaigns.map((campaign, index) => (
											<tr key={campaign.id}>
												<td>{index + 1}</td>
												<td>
													<strong>{campaign.subject}</strong>
													{campaign.preview_text && (
														<div className="text-muted small">{campaign.preview_text.substring(0, 50)}...</div>
													)}
												</td>
												<td>
													<Badge bg="info">
														{campaign.campaign_type === 'breaking_news' ? 'Breaking News' : campaign.campaign_type}
													</Badge>
												</td>
												<td>{getCampaignStatusBadge(campaign.status)}</td>
												<td>{campaign.total_recipients}</td>
												<td>{campaign.total_sent}</td>
												<td>{campaign.total_clicked}</td>
												<td>{moment(campaign.created_at).format('MMM D, YYYY')}</td>
												<td className="text-end">
													<div className="d-flex gap-2 justify-content-end">
														<Button
															size="sm"
															variant="warning"
															onClick={() => navigate(`/admin-panel/email-marketing/edit/${campaign.id}`)}
															title="Edit Campaign"
														>
															Edit
														</Button>
														{campaign.status !== 'sent' && (
															<Button
																size="sm"
																variant="success"
																onClick={() => handleSendCampaign(campaign.id)}
																title="Send Now"
															>
																Send
															</Button>
														)}
														{campaign.status === 'sent' && (
															<Button
																size="sm"
																variant="primary"
																onClick={() => handleResendCampaign(campaign.id, campaign.subject)}
																title="Resend Campaign"
															>
																Resend
															</Button>
														)}
														<Button
															size="sm"
															variant="danger"
															onClick={() => handleDeleteCampaign(campaign.id, campaign.subject)}
															title="Delete Campaign"
														>
															Delete
														</Button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							)}
						</Tab>

						{/* Users Tab */}
						<Tab eventKey="users" title="Users">
							<div className="d-flex justify-content-between align-items-center mb-3">
								<h5 className="mb-0">Registered Users ({getFilteredUsersForTab().length})</h5>
								<Form.Control
									type="text"
									placeholder="Search by email or name..."
									value={userTabSearchTerm}
									onChange={(e) => {
										setUserTabSearchTerm(e.target.value);
										setUserPage(1); // Reset to first page on search
									}}
									style={{ maxWidth: '300px' }}
								/>
							</div>

							{getFilteredUsersForTab().length === 0 ? (
								<div className="text-center py-5">
									<p className="text-muted">{userTabSearchTerm ? 'No users found matching your search' : 'No users found'}</p>
								</div>
							) : (
								<>
									<Table responsive hover>
										<thead>
											<tr>
												<th>#</th>
												<th>Email</th>
												<th>Name</th>
												<th>Email Verified</th>
												<th>Breaking News</th>
												<th>Newsletter</th>
												<th>Registered</th>
											</tr>
										</thead>
										<tbody>
											{getFilteredUsersForTab()
												.slice((userPage - 1) * usersPerPage, userPage * usersPerPage)
												.map((user, index) => (
													<tr key={user.id}>
														<td>{(userPage - 1) * usersPerPage + index + 1}</td>
														<td>{user.email}</td>
														<td>{user.full_name || user.first_name || '-'}</td>
														<td>
															{user.email_verified === 1 ? (
																<Badge bg="success">Verified</Badge>
															) : (
																<Badge bg="warning">Not Verified</Badge>
															)}
														</td>
														<td>
															<Form.Check
																type="switch"
																checked={user.email_breaking_news !== false}
																onChange={(e) => handleToggleEmailPreference(user.id, 'email_breaking_news', e.target.checked)}
															/>
														</td>
														<td>
															<Form.Check
																type="switch"
																checked={user.email_newsletter !== false}
																onChange={(e) => handleToggleEmailPreference(user.id, 'email_newsletter', e.target.checked)}
															/>
														</td>
														<td>{moment(user.date_created).format('MMM D, YYYY')}</td>
													</tr>
												))}
										</tbody>
									</Table>

									{/* Pagination */}
									{getFilteredUsersForTab().length > usersPerPage && (
										<div className="d-flex justify-content-center mt-3">
											<Pagination>
												<Pagination.First onClick={() => setUserPage(1)} disabled={userPage === 1} />
												<Pagination.Prev onClick={() => setUserPage(userPage - 1)} disabled={userPage === 1} />
												
												{[...Array(Math.ceil(getFilteredUsersForTab().length / usersPerPage))].map((_, i) => {
													const pageNum = i + 1;
													const totalPages = Math.ceil(getFilteredUsersForTab().length / usersPerPage);
													
													// Show first page, last page, current page, and pages around current
													if (
														pageNum === 1 ||
														pageNum === totalPages ||
														(pageNum >= userPage - 1 && pageNum <= userPage + 1)
													) {
														return (
															<Pagination.Item
																key={pageNum}
																active={pageNum === userPage}
																onClick={() => setUserPage(pageNum)}
															>
																{pageNum}
															</Pagination.Item>
														);
													} else if (pageNum === userPage - 2 || pageNum === userPage + 2) {
														return <Pagination.Ellipsis key={pageNum} disabled />;
													}
													return null;
												})}

												<Pagination.Next 
													onClick={() => setUserPage(userPage + 1)} 
													disabled={userPage === Math.ceil(getFilteredUsersForTab().length / usersPerPage)} 
												/>
												<Pagination.Last 
													onClick={() => setUserPage(Math.ceil(getFilteredUsersForTab().length / usersPerPage))} 
													disabled={userPage === Math.ceil(getFilteredUsersForTab().length / usersPerPage)} 
												/>
											</Pagination>
										</div>
									)}
								</>
							)}
						</Tab>
					</Tabs>
				</Card.Body>
			</Card>

			{/* Schedule Campaign Modal */}
			<Modal 
				show={showScheduleModal} 
				onHide={() => {
					setShowScheduleModal(false);
					setCampaignForm({ scheduled_at: '' });
					setSelectedUsers([]);
					setSelectAll(false);
				}} 
				size="lg"
				centered
			>
				<Modal.Header closeButton className="bg-info text-white">
					<Modal.Title>Schedule Campaign</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{selectedCampaign && (
						<div>
							<div className="mb-4 p-3 bg-light rounded">
								<h6 className="mb-2">Campaign Details</h6>
								<p className="mb-1"><strong>Subject:</strong> {selectedCampaign.subject}</p>
								<p className="mb-0"><strong>Type:</strong> {selectedCampaign.campaign_type}</p>
							</div>

							<Form.Group className="mb-3">
								<Form.Label>Schedule Date & Time *</Form.Label>
								<Form.Control
									type="datetime-local"
									value={campaignForm.scheduled_at}
									onChange={(e) => setCampaignForm({ ...campaignForm, scheduled_at: e.target.value })}
									min={new Date().toISOString().slice(0, 16)}
									required
								/>
								<Form.Text className="text-muted">
									Select when you want this campaign to be sent
								</Form.Text>
							</Form.Group>

							<Form.Group className="mb-3">
								<div className="d-flex justify-content-between align-items-center mb-2">
									<Form.Label className="mb-0">Select Recipients</Form.Label>
									<Button 
										size="sm" 
										variant="outline-primary"
										onClick={openUserSelectionModal}
									>
										Select Users ({selectedUsers.length})
									</Button>
								</div>
								<Form.Text className="text-muted">
									{selectedUsers.length === 0 
										? 'Will send to all active subscribers' 
										: `Will send to ${selectedUsers.length} selected user(s)`}
								</Form.Text>
							</Form.Group>

							<div className="d-flex justify-content-end gap-2">
								<Button variant="secondary" onClick={() => {
									setShowScheduleModal(false);
									setCampaignForm({ scheduled_at: '' });
									setSelectedUsers([]);
									setSelectAll(false);
								}}>
									Cancel
								</Button>
								<Button variant="info" onClick={handleSaveSchedule}>
									Schedule Campaign
								</Button>
							</div>
						</div>
					)}
				</Modal.Body>
			</Modal>

			{/* User Selection Modal */}
			<Modal 
				show={showUserSelectionModal} 
				onHide={closeUserSelectionModal} 
				size="lg"
				centered
			>
				<Modal.Header closeButton className="bg-success text-white">
					<Modal.Title>Select Recipients</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-3">
						<Form.Control
							type="text"
							placeholder="Search users by email or name..."
							value={userSearchTerm}
							onChange={(e) => setUserSearchTerm(e.target.value)}
						/>
					</div>

					<div className="mb-3 d-flex justify-content-between align-items-center">
						<Form.Check
							type="checkbox"
							label={`Select All Active Users (${getFilteredUsers().filter(u => u.email_breaking_news !== false && u.email_verified === 1).length})`}
							checked={selectAll}
							onChange={handleSelectAllUsers}
						/>
						<Badge bg="primary">{selectedUsers.length} selected</Badge>
					</div>

					<div style={{ maxHeight: '400px', overflowY: 'auto' }}>
						<Table hover size="sm">
							<thead className="sticky-top bg-white">
								<tr>
									<th style={{ width: '50px' }}>Select</th>
									<th>Email</th>
									<th>Name</th>
									<th>Email Verified</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{getFilteredUsers().map((user) => (
									<tr key={user.id}>
										<td>
											<Form.Check
												type="checkbox"
												checked={selectedUsers.includes(user.id)}
												onChange={() => handleToggleUserSelection(user.id)}
												disabled={user.email_breaking_news === false || user.email_verified !== 1}
											/>
										</td>
										<td>{user.email}</td>
										<td>{user.full_name || user.first_name || '-'}</td>
										<td>
											{user.email_verified === 1 ? (
												<Badge bg="success">Verified</Badge>
											) : (
												<Badge bg="warning">Not Verified</Badge>
											)}
										</td>
										<td>
											{user.email_breaking_news === false ? (
												<Badge bg="secondary">Disabled</Badge>
											) : user.email_verified === 0 ? (
												<Badge bg="warning">Not Verified</Badge>
											) : (
												<Badge bg="success">Active</Badge>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					</div>

					<div className="d-flex justify-content-end gap-2 mt-3">
						<Button variant="secondary" onClick={closeUserSelectionModal}>
							Cancel
						</Button>
						<Button variant="success" onClick={closeUserSelectionModal}>
							Confirm Selection ({selectedUsers.length})
						</Button>
					</div>
				</Modal.Body>
			</Modal>

		</Container>
	);
}

const mapStateToProps = (state) => ({
	user: state.user
});

export default connect(mapStateToProps)(AdminEmailMarketing);
